import { useEffect, useState } from 'react'

// The countdown is shown only inside the last 24 hours of a promotion. That threshold is a single
// global instant rather than each visitor's local midnight, so every bucketed user is exposed for
// the same real interval regardless of timezone — see the experiment's implementation plan.
const WINDOW_MS = 24 * 60 * 60 * 1000
const TICK_MS = 1000

/**
 * Milliseconds left until `endsAt`, or null when the countdown should not be shown: no end
 * instant, more than a day left, or the promotion already ended.
 */
export const useCountdown = (endsAt?: string) => {
  const [remainingMs, setRemainingMs] = useState<number | null>(null)

  useEffect(() => {
    const endMs = endsAt ? Date.parse(endsAt) : NaN

    if (Number.isNaN(endMs)) {
      setRemainingMs(null)
      return undefined
    }

    // Read the clock in an effect, never during render: the urgency copy this replaces already
    // resolves post-hydration, so server and client markup stay identical. Outside the window
    // this keeps setting null, which React bails out of — no re-render until the window opens.
    const tick = () => {
      const remaining = endMs - Date.now()

      setRemainingMs(remaining > 0 && remaining < WINDOW_MS ? remaining : null)
    }

    tick()
    const interval = setInterval(tick, TICK_MS)

    return () => clearInterval(interval)
  }, [endsAt])

  return remainingMs
}
